import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";

export const ALERTS_KEY = ["alerts"] as const;

const utc = (t: string) => `${t.slice(0, 19).replace("T", " ")} UTC`;

/** Open alerts, polled for the banner. */
export function useOpenAlerts(enabled: boolean) {
  return useQuery({
    queryKey: [...ALERTS_KEY, "open"],
    queryFn: () =>
      unwrap(api.GET("/api/alerts", { params: { query: { open: true } } })),
    enabled,
    refetchInterval: 60_000,
  });
}

/** Alerts for auditors (threat model C13), e.g. device uploads that look wrong. */
export function AlertsPage() {
  const qc = useQueryClient();
  const alerts = useQuery({
    queryKey: ALERTS_KEY,
    queryFn: () => unwrap(api.GET("/api/alerts")),
  });
  const ack = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/alerts/{id}/acknowledge", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
  return (
    <Stack spacing={3} component="section" aria-label="Alerts">
      <Typography variant="h5" component="h2">
        Alerts
      </Typography>
      <Typography color="text.secondary">
        Device uploads that look wrong: repeated refused sign-ins, a source a
        device hasn&apos;t uploaded before, recording times far in the past or
        future, or far more files than usual. A stolen device credential could
        plant recordings that look genuine, so check each one and acknowledge
        it.
      </Typography>
      {alerts.isError && <Alert severity="error">{alerts.error.message}</Alert>}
      {ack.isError && <Alert severity="error">{ack.error.message}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Alert list">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>What happened</TableCell>
              <TableCell>Acknowledged</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No alerts.</TableCell>
              </TableRow>
            )}
            {alerts.data?.map((a) => (
              <TableRow
                key={a.id}
                sx={
                  a.acknowledged_at ? undefined : { bgcolor: "warning.light" }
                }
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {utc(a.created_at)}
                </TableCell>
                <TableCell>{a.subject}</TableCell>
                <TableCell>{a.message}</TableCell>
                <TableCell>
                  {a.acknowledged_at ? (
                    `${a.acknowledged_by ?? "Someone"}, ${utc(a.acknowledged_at)}`
                  ) : (
                    <Button
                      size="small"
                      disabled={ack.isPending}
                      onClick={() => ack.mutate(a.id)}
                    >
                      Acknowledge
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
